import { getCentralSystem } from '@infuse/api/e-connector/central-system';
import { linkChargingStation, LinkRequest } from '@infuse/api/e-connector/charging-station';
import { useNotify } from '@infuse/cpms/ui';
import { LinkedCentralSystem } from '@infuse/e-connector/central-system/domain';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useUpsertCentralSystemLink = () => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyFailure } = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  return useMutation({
    mutationFn: async ({
      linkedCentralSystem,
      shouldRemoveLink = false
    }: {
      linkedCentralSystem: LinkedCentralSystem;
      shouldRemoveLink?: boolean;
    }) => {
      const centralSystem = await getCentralSystem({ id: linkedCentralSystem.centralSystemId.toString() });
      const linkRequest = createLinkRequestFor(linkedCentralSystem, centralSystem.name, shouldRemoveLink);
      return linkChargingStation({ id: linkedCentralSystem.chargingStationId }, linkRequest);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['charging-stations'] });

      notifySuccess({
        title: t('e_connector.common.notify.success'),
        message: t('e_connector.chargingStationLinks.notify.update.success')
      });
    },
    onError: (error) => {
      notifyFailure({
        title: t('e_connector.common.notify.error'),
        message: t('e_connector.chargingStationLinks.notify.update.error', {
          reason: getAxiosErrorMessage(error, '')
        })
      });
    }
  });
};

export const createLinkRequestFor = (
  linkedCentralSystem: LinkedCentralSystem,
  centralSystemName: string,
  shouldRemoveLink: boolean
): LinkRequest => {
  const linkRequest = {
    centralSystemName, // why oh why do we need to use the name instead of the id?
    link: !shouldRemoveLink,
    type: linkedCentralSystem.type,
    customChargePointName: linkedCentralSystem.customChargingStationName,
    commandsAllowed: linkedCentralSystem.commandsAllowed,
    sessionAuthorizationAllowed: linkedCentralSystem.sessionAuthorizationAllowed,
    forwardBasicAuth: linkedCentralSystem.forwardBasicAuth
  } as LinkRequest;

  if (!linkedCentralSystem.customChargingStationName) {
    linkRequest.customChargePointName = undefined;
  }

  return linkRequest;
};
